import Dialog from '@material-ui/core/Dialog';
import classnames from 'classnames';
import gql from 'graphql-tag';
import React, {useState, useRef} from 'react';
import {useMutation, useQuery} from 'react-apollo';
import {useTranslation} from 'react-i18next';
import useJourneyStepMachine from 'utils/hooks/useJourneyStepMachine';
import Button from '../../components/Button/Button';
import Loading from '../../components/Loading/Loading';
import SideBarPanelNav from '../../components/SideBarPanelNav/SideBarPanelNav';
import Step from '../../components/Step/Step';
import { updateMirror } from '../../graphql/mutations';
import { getRelationship } from '../../graphql/queries';
import EmojiToImage from '../../utils/EmojiToImage';
import { debounce } from 'lodash';
import './Quest.scss';
import { track } from '../../utils/mixpanel';
import {buildJourneyFromStepIds} from "../Journey/JourneyFlowItems";
import {getPlanFromAIChat} from "../../utils/helpers";
import Backgrounds from 'components/Backgrounds/Backgrounds';
import Stage from 'components/Stage/Stage';
import * as ANIMALS from "../../components/Animal/AnimalAssets";

const MUTATE_DEBOUNCE_DELAY_IN_MS = 1000;

function Quest(props) {
  const toggleEditDialog = (open, setIsEditDialogOpen) => (event) => {
    setIsEditDialogOpen(open);
  };

  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const openEditDialog = React.useCallback(() => {
    setIsEditDialogOpen(true);
  }, [setIsEditDialogOpen]);

  const {t} = useTranslation();
  const questFormRef = useRef();
  const {match, relationship} = props;
  const {params} = match;
  const {id, journeyID, questID: questIDStr} = params;
  const relationshipID = id;
  const questID = Number(questIDStr);
  const {mySideMirrors, name, partnerName, gender, partnerGender} = relationship;
  const intialQuestData = mySideMirrors.find((m) => m.id === journeyID);

  const [state, send] = useJourneyStepMachine(intialQuestData, questID);
  const journeyFlow = buildJourneyFromStepIds(state.context.journey.journeyStepIds)
  const questFlow = journeyFlow[questID];

  const [updateQuestMutation] = useMutation(gql(updateMirror));
  const [updateQuestLoading, setUpdateQuestLoading] = useState(false);
  
  // Memoize the debounced function so it persists across renders
  const debouncedUpdateQuestMutation = React.useMemo(
    () => debounce(updateQuestMutation, MUTATE_DEBOUNCE_DELAY_IN_MS),
    [updateQuestMutation]
  );

  // For auto-saves (typing etc)
  const mutateQuestData = React.useCallback(
    (questDataToSave) => {
       // We don't typically show loading for background debounced saves, 
       // or if we do, we can't easily await the debounced execution.
       debouncedUpdateQuestMutation({variables: {input: questDataToSave}});
    },
    [debouncedUpdateQuestMutation]
  );

  // For explicit button clicks (Next/Save)
  const mutateQuestDataImmediate = React.useCallback(
    async (questDataToSave) => {
      setUpdateQuestLoading(true);
      try {
        // Cancel any pending debounced calls to avoid race conditions
        debouncedUpdateQuestMutation.cancel(); 
        await updateQuestMutation({variables: {input: questDataToSave}});
      } catch (e) {
        console.error("Failed to save quest data", e);
        // Optionally show alert or toast here
        alert(t("general.saveFailed")); // Simple feedback
      } finally {
        setUpdateQuestLoading(false)
      }
    },
    [updateQuestMutation, debouncedUpdateQuestMutation, t]
  );

  const getStepItemsFlow = React.useCallback(
    (stepIdx) => {
      return questFlow.steps[stepIdx].stepItems.map((stepItem) => {
        return stepItem;
      });
    },
    [questFlow]
  );

  const {
    selectedStepNumberContext,
    stepsContext,
    CTAtextContext,
    isCTAContext,
    isBackContext,
    isExitContext,
    journey: questData
  } = state.context;

  const animalId = relationship.animalId;
  const partnerAnimalId = relationship.partnerAnimalId;

  const setCssVar = (mySideColor, partnerColor) => {
    document.documentElement.style.setProperty('--mySideColor', mySideColor);
    document.documentElement.style.setProperty('--partnerColor', partnerColor);
  };
  // Track quest (journey item) completion once when exit overlay is shown
  const questCompletedTrackedRef = useRef(false);
  if (isExitContext && !questCompletedTrackedRef.current) {
    try { track('APP_Felt_Quest_Completed', { relationshipID, journeyID, questID }); } catch(_) {}
    questCompletedTrackedRef.current = true;
  }
  

  return (
    <>
      <Dialog open={isEditDialogOpen} onClose={toggleEditDialog(false, setIsEditDialogOpen)}>
        <div className="modal-buttons modal-buttons-container">
          <Button
            isLink={true}
            to={`/relationship/${relationshipID}/journey/${journeyID}`}
            text={t('mirroring.pause.exit')}
          />
          <p className="micro-text micro-text__white">{t('mirroring.pause.exit-minitext')}</p>
          <Button
            className="close"
            text={t(`general.back`)}
            onClick={() => setIsEditDialogOpen(false)}
          />
        </div>
      </Dialog>
      <div className={'exitOverlay ' + (isExitContext ? '' : 'displayNone')}>
        <div className="overlayBox noselect">
          <div className="questCompletedImg noselect">
            <div className="v-container add-bounce-animation noselect">
              <div className="v-icon">{EmojiToImage("v", false)}</div>
            </div>
            <div className={"img noselect "}>{EmojiToImage(questFlow.icon, false)}</div>
            <div className={"circle noselect"}></div>
          </div>

          <h3 className="exitOverlay-topTitle fade-in_slow-1 noselect">{t("general.questCompleted")}</h3>
          <h2 className="exitOverlay-bottomTitle noselect fade-in_slow-2">{t(questFlow.title, {
            name: name,
            partnerName: partnerName
          })}</h2>
          <h4 style={{color: 'white'}}>
            {getPlanFromAIChat(questData.AISummary)}
          </h4>
          {!updateQuestLoading && <Button
            classes="button-primary button3D exitOverlay-button fade-in_slow-3"
            text={t(`general.continue`)}
            isLink={true}
            to={`/relationship/${relationshipID}/journey/${journeyID}`}
          />}
        </div>
      </div>
      <div className="questContent">
        <SideBarPanelNav
          left1={'pauseButton'}
          right={'progressBar'}
          rightAction={openEditDialog}
          selectedStepNumberContext={selectedStepNumberContext}
          stepsArray={stepsContext}
          leftTo={`/relationship/${relationshipID}/journey/${journeyID}`}
        />
        <div className="questForm" ref={questFormRef}>
          {stepsContext.map((stepContext, stepIdx) => (
            <Step
              key={stepIdx}
              // title={t(questFlow.steps[stepIdx].title, {
              //   name: name,
              //   partnerName: partnerName
              // })}
              stepItemsFlow={getStepItemsFlow(stepIdx)}
              questData={questData}
              isSelected={stepIdx === selectedStepNumberContext}
              stepNumber={stepIdx}
              stepContext={stepContext}
              stepFlow={questFlow.steps[stepIdx]}
              name={name}
              side1Animal={relationship.animalId}
              side1Gender={gender}
              side2Animal={relationship.partnerAnimalId}
              side2Gender={partnerGender}
              partnerName={partnerName}
              relation={relationship.relation}
              relationship={relationship}
              send={send}
              saveQuestData={mutateQuestData}
              questFormRef={questFormRef}
            />
          ))}
          <div className="MINI-STEP__STAGE--mobile stageAndBackground ">
              <Backgrounds
                // name = {'Hills'} // Hills,
                name={'Room'} // Hills,
                classes={"ingame-background"}
              />

              <Stage
                name={name}
                partnerName={partnerName}
                animalId={animalId}
                partnerAnimalId={partnerAnimalId}
                classes=""
                showSides={true}
                mySide_distance={'5%'}
                partnerSide_distance={'5%'}
                background={'hills'}
                // isBummer={true}
              />
              <div className="stageBackground-container stageBackground-container-3d ">
                <div className="stageBackground stageBackground-3d "></div>
              </div>
              {setCssVar(ANIMALS.default[animalId].color, ANIMALS.default[partnerAnimalId].color)}
            </div>
        </div>
        <div className="questButtons__container noselect">
          {/* <div className="questButtons-gradient"></div> */}
          <div className="questButtons">

            {isBackContext ? (
              <button
                className={'button button-secondary back'}
                onClick={() => {
                  try { track('APP_Felt_Quest_Back', { relationshipID, journeyID, stepIndex: state.context.selectedStepNumberContext }); } catch(_) {}
                  send({type: 'BACK'});
                }}
              >
                {t('general.back')}
              </button>
            ) : (
              ''
            )}
            <button
              className={classnames('button button3D button-primary cta', {
                buttonDisabled: !isCTAContext
              })}
              onClick={() => {
                const {
                  context: {journey}
                } = send({type: 'CTA'});
                try { track('APP_Felt_Quest_CTA', { relationshipID, journeyID, questID, stepIndex: state.context.selectedStepNumberContext, selections: journey }); } catch(_) {}
                mutateQuestDataImmediate(journey);
              }}
            >
              {t(`general.${CTAtextContext}`)}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

function QuestWrapper(props) {
  const {match} = props;
  const {loading, error, data} = useQuery(gql(getRelationship), {
    variables: {id: match.params.id}
  });

  if (loading) {
    return <Loading/>;
  }
  return <Quest {...props} relationship={data.getRelationship}/>;
}

export default QuestWrapper;
